import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { Dispatch, useCallback, useMemo, useState } from 'react';

import { ShowMoreButton } from 'UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { KNM_RESULT_DECISIONS } from 'CorePackage/MainModule/routes/routes';
import {
  getResultDecisionBlockId,
  RESULT_DECISIONS_BLOCK_ID,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmOrganizationTemplateSelectOption } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmOrganizationTemplateSelectOption';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmExternalParams,
  getKnmFormRef,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsNeedActSaveOrSign,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { WAS_REMOVE_DUPLICATE_VIOLATIONS } from 'CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { getRequirementsWithNonFixedViolations } from 'KnmErknmPackage/ErknmViolationsModule/common/getRequirementsWithNonFixedViolations';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';

import { ErknmResultDecisionsItem } from './ErknmResultDecisionsItem';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IKnmErknmFormViolatedRequirementsNpa } from '../../MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { ErknmResultDecisionsViolatedRequirementsModal } from './ErknmResultDecisionsViolatedRequirementsModal';
import { IDictionaryKnoOrganizationSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IKnmErknmFormAct } from '../../MainModule/interfaces/IKnmErknmFormAct';
import { IKnmErknmFormEvent } from '../../MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormSubject } from '../../MainModule/interfaces/IKnmErknmFormSubject';
import { useShowMore } from '../../../hooks/useShowMore';
import { Spinner } from '../../../UIPackage/MainModule/components/Spinner';
import styles from '../../ErknmPlanHistoryModule/components/ErknmPlanHistory.module.scss';

interface IErknmResultDecisionsProps {
  watchOnlyExtended: boolean;
  kindKnmCode: KnmKindCodesEnum | undefined;
  organizationsTemplatesSelectOptions: IKnmErknmOrganizationTemplateSelectOption[] | undefined;
  resultDecisions: IKnmErknmFormResultDecision[] | undefined;
  created: string | undefined;
  knoOrganization?: IDictionaryKnoOrganizationSelectOption;
  acts: IKnmErknmFormAct[] | undefined;
  eventsErknm?: IKnmErknmFormEvent[] | undefined;
  organizations?: IKnmErknmFormSubject[] | undefined;
  isKnmKindRO: boolean;
  actsValue: IKnmErknmFormAct[] | undefined;
  agreements: IErknmFormAgreement[] | undefined;
}

interface IErknmResultDecisionsStoreProps {
  formRef: IKnmFormRef | undefined;
  isKnmStatusTypeCarriedOut: boolean;
  settings: IAdminSetting[] | undefined;
  isNeedActSaveOrSign?: boolean;
  knmExternalParams?: Record<string, string>;
}

const ErknmResultDecisionsWithoutStore = ({
  watchOnlyExtended,
  kindKnmCode,
  organizationsTemplatesSelectOptions,
  resultDecisions,
  formRef,
  organizations,
  isKnmStatusTypeCarriedOut,
  created,
  settings,
  knoOrganization,
  acts,
  eventsErknm,
  isKnmKindRO,
  actsValue,
  isNeedActSaveOrSign,
  agreements,
  knmExternalParams,
}: IErknmResultDecisionsProps & IErknmResultDecisionsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const isShow4127 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_27_STARTS_FROM,
  );

  const isShow4155 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_55_STARTS_FROM,
  );

  const isLaterThanViolationsInProd = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.VALIDATION_4_1_29_STARTS_FROM,
  );

  const isLater4111ForDecision = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.DECISION_SUBJECT_TYPE_4_1_1_START_FROM,
  );

  /**
   * Паспорт создан после ввода в эксплуатацию требования 4.1.3 (безбумажные акт и предписание)
   */
  const isEnablePaperless4113 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  const isEnablePaperlessSetting =
    settings?.find(setting => setting.code === AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_ACT_PRESCRIPTION)
      ?.value === 'true';

  const isPaperless = useMemo(() => {
    if (isKnmKindRO && resultDecisions?.length) {
      return resultDecisions.some(i => i.paperless);
    }
    /**
     * Предписание всегда привязано к акту, поэтому вид уже созданного акта первичен
     */
    if (acts?.length) {
      return acts.some(act => !!act.paperless);
    }

    return isEnablePaperless4113 && isEnablePaperlessSetting;
  }, [isKnmKindRO, resultDecisions, acts, isEnablePaperless4113, isEnablePaperlessSetting]);

  const isWatchOnly = useMemo(() => {
    // Есть ли хотя бы одно нарушенное обязательство
    const isViolatedMandatoryTrue = !acts?.find(i => i.isViolatedMandatoryRequirements?.value);

    return (
      watchOnlyExtended ||
      (isShow4155 && isKnmStatusTypeCarriedOut) ||
      (isShow4127 && (!acts || isViolatedMandatoryTrue))
    );
  }, [watchOnlyExtended, isKnmStatusTypeCarriedOut, isShow4155, isShow4127, acts]);

  const isExpandAllInPrescription = knmExternalParams?.expandAllInPrescription === 'true';

  const addResultDecision = useCallback(() => {
    if (!isWatchOnly && push) {
      const newResultDecision: IKnmErknmFormResultDecision = {
        status: undefined,
        agreementStatus: undefined,
        document: undefined,
        extractVersion: undefined,
        protocol: undefined,
        kindDecision: undefined,
        punishmentNature: undefined,
        noteLawsuits: undefined,
        numberDecision: undefined,
        injunction: undefined,
        dateDecision: undefined,
        fioSigner: undefined,
        dateResolvedByAgreement: undefined,
        titleSigner: undefined,
        inspectors: undefined,
        subjectDecision: undefined,
        subjectIsAutocomplete: undefined,
        responsibleEntities: undefined,
        isProsecution: undefined,
        guid: undefined,
        dateCreate: undefined,
        listId: uuidv4(),
        isNew: true,
        organizationTemplate: undefined,
        organization: organizations?.length === 1 ? organizations[0] : undefined,
        violatedMandatoryRequirements: undefined,
        act: undefined,
        signed: undefined,
        familiarizationNotifications: undefined,
        paperless: isPaperless,
        firstExtractDateTime: null,
        systemSignatureUsed: false,
        systemSignatureDateTime: null,
        systemSignatureMnemonic: null,
        systemSignatureTitle: null,
      };

      push('resultDecisions', newResultDecision);
    }
  }, [push, isWatchOnly, organizations, isPaperless]);

  const [isViolatedRequirementsNpaModalVisible, setIsViolatedRequirementsNpaModalVisible] = useState(false);
  const [decisionIndex, setDecisionIndex] = useState(-1);
  const [violatedRequirementsNpaFieldName, setViolatedRequirementsNpaFieldName] = useState('');
  const openViolatedRequirementsNpaModal = (index: number, fieldName: string) => {
    setViolatedRequirementsNpaFieldName(fieldName);
    setDecisionIndex(index);
    setIsViolatedRequirementsNpaModalVisible(true);
  };

  const addViolatedRequirementsNpa = (items: IKnmErknmFormViolatedRequirementsNpa[]) => {
    const requirements =
      // @ts-ignore
      (formRef?.current?.getFieldState(violatedRequirementsNpaFieldName)
        ?.value as IKnmErknmFormViolatedRequirementsNpa[]) || [];
    let hasFoundDuplicate = false;

    if (push) {
      items.forEach(item => {
        if (isPaperless) {
          const updatedItem = {
            ...item,
            violatedMandatory: item.violatedMandatory.map(vm => ({
              ...vm,
              violations: vm.violations?.map(vio => ({
                ...vio,
                fixPlanDates: [
                  {
                    isNew: true,
                    value: '',
                    guid: null,
                    dateCreate: null,
                  },
                ],
                fixPlanMethods: [],
                fixPlanExplanations: [],
              })),
            })),
          };

          const npaIndex = requirements.findIndex(
            req => req.npa.id === item.npa.id && req.structuralUnit.id === item.structuralUnit.id,
          );

          if (npaIndex > -1) {
            hasFoundDuplicate = true;
          } else {
            push(violatedRequirementsNpaFieldName, updatedItem);
          }
        } else {
          push(violatedRequirementsNpaFieldName, item);
        }
      });
    }

    if (hasFoundDuplicate) {
      dispatch(
        addNoticeAction({
          renderContent: WAS_REMOVE_DUPLICATE_VIOLATIONS,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.NPA_CHECK,
        }),
      );
    }
  };

  const makeRemoveResultDecisionHandler = (index: number) => () =>
    !watchOnlyExtended && remove && remove('resultDecisions', index);

  const actsRequirementsNpa = (isUnresolvedOnly: boolean, decisionIdx: number = decisionIndex) => {
    let requirementsNpa: IKnmErknmFormViolatedRequirementsNpa[] = [];

    const resultDecision: IKnmErknmFormResultDecision | undefined = formRef?.current.getFieldState('resultDecisions')
      ?.value?.[decisionIdx];

    if (resultDecision) {
      formRef?.current
        .getFieldState('acts')
        ?.value?.filter(x => {
          return (
            x.violatedMandatoryRequirements &&
            (kindKnmCode !== KnmKindCodesEnum.RO ||
              (kindKnmCode === KnmKindCodesEnum.RO &&
                x.organizationTemplate?.value === resultDecision.organizationTemplate?.value))
          );
        })
        ?.map(x => x.violatedMandatoryRequirements)
        .forEach(x => {
          if (x) {
            requirementsNpa = requirementsNpa.concat(x);
          }
        });
    }

    return isUnresolvedOnly ? getRequirementsWithNonFixedViolations(requirementsNpa) : requirementsNpa;
  };

  const isButtonAddDisabled = useMemo(() => {
    const isNotCanceledAct = !!acts?.find(i => !i.fileInvalidate);

    return !isPaperless ? isWatchOnly : isWatchOnly || !isNotCanceledAct || isNeedActSaveOrSign;
  }, [isWatchOnly, acts, isNeedActSaveOrSign, isPaperless]);

  return (
    <Section
      title="Сведения о решениях, принятых по результатам КНМ"
      type="list"
      id={KNM_RESULT_DECISIONS}
      titleId={RESULT_DECISIONS_BLOCK_ID}
      customButton={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={isButtonAddDisabled}
          onClick={addResultDecision}
        />
      }
    >
      <FieldArray name="resultDecisions">
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormResultDecision, HTMLElement>) => {
          const { visibleItems, hasMore, showMore, totalCount, visibleCount, isRendering } = useShowMore(fields.value, {
            initialCount: 10,
            incrementCount: 10,
          });

          if (!fields.length) {
            return null;
          }

          return (
            <>
              {visibleItems.map((fieldValue: IKnmErknmFormResultDecision) => {
                const index =
                  fields.value?.findIndex((item: IKnmErknmFormResultDecision) => item.listId === fieldValue.listId) ??
                  0;
                const name = `resultDecisions[${index}]`;
                const id = getResultDecisionBlockId(fieldValue.listId);

                return (
                  <ErknmResultDecisionsItem
                    formRef={formRef}
                    value={fieldValue}
                    key={id}
                    id={id}
                    name={name}
                    acts={acts}
                    isShow4127={isShow4127}
                    eventsErknm={eventsErknm}
                    organizations={organizations}
                    decisionIndex={index}
                    watchOnly={isWatchOnly}
                    kindKnmCode={kindKnmCode}
                    knoOrganizationTitle={knoOrganization?.title}
                    knoOrganizationPhone={knoOrganization?.phone}
                    knoOrganizationAddress={knoOrganization?.address}
                    organizationsTemplatesSelectOptions={organizationsTemplatesSelectOptions}
                    resultDecisions={resultDecisions}
                    onRemove={makeRemoveResultDecisionHandler(index)}
                    addRequirementsNpa={openViolatedRequirementsNpaModal}
                    actsRequirementsNpa={actsRequirementsNpa(false, index)}
                    isLaterThanViolationsInProd={isLaterThanViolationsInProd}
                    isPaperless={!!fieldValue.paperless}
                    actsValue={actsValue}
                    agreements={agreements}
                    isLater4111ForDecision={isLater4111ForDecision}
                    isExpandAllInPrescription={isExpandAllInPrescription}
                  />
                );
              })}
              {isRendering && <Spinner className={styles.Loader} />}
              {hasMore && !isRendering && (
                <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />
              )}
            </>
          );
        }}
      </FieldArray>
      <ErknmResultDecisionsViolatedRequirementsModal
        requirementsNpa={actsRequirementsNpa(isLaterThanViolationsInProd)}
        visible={isViolatedRequirementsNpaModalVisible}
        onClose={() => setIsViolatedRequirementsNpaModalVisible(false)}
        onSubmit={addViolatedRequirementsNpa}
      />
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IErknmResultDecisionsStoreProps => ({
  formRef: getKnmFormRef(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  settings: getAdminFetchSettings(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  knmExternalParams: getKnmExternalParams(state),
});

export const ErknmResultDecisions = connect(mapStateToProps)(ErknmResultDecisionsWithoutStore);
